/**
 * Theme-specific emblem / motif helpers — for v2 hero & decorative slots.
 *
 * 不要跟 components/* 的 TarotCard / Avatar / Logo / SectionHeading 衝突。
 * 這裡放純 SVG 裝飾元件（hero emblem、persona 卡 motif、藍底星空），
 * 由 pages 直接 import 使用。
 *
 * Theme 由 <html data-theme> 推導，pages 只要傳 theme 進來，這裡負責畫對的東西。
 */

import type { Theme } from './personas';

/**
 * Hero emblem — landing 首屏中央那顆「不是色塊的真 motif」。
 *
 * - mystic    art-deco sun/moon 鉑金徽章（呼應 prototype mystic.jsx landing-hero）
 * - minimal   三張線稿 schematic 牌堆
 * - morandi   三顆鵝卵石 / 圓角陶土卡
 */
export function HeroEmblem({ theme, className }: { theme: Theme; className?: string }) {
  if (theme === 'mystic') {
    return (
      <svg
        viewBox="0 0 200 140"
        className={className}
        aria-hidden
        style={{ opacity: 0.92 }}
      >
        <g transform="translate(100 74)">
          <circle r="44" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
          <circle r="34" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.4" />
          <path d="M 0 -38 L 5 0 L 0 38 L -5 0 Z" fill="currentColor" opacity="0.85" />
          <path d="M -38 0 L 0 4 L 38 0 L 0 -4 Z" fill="currentColor" opacity="0.5" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
            <line
              key={a}
              x1="0"
              y1="0"
              x2="0"
              y2="-44"
              transform={`rotate(${a})`}
              stroke="currentColor"
              strokeWidth="0.3"
              opacity="0.3"
            />
          ))}
          <circle r="4" fill="var(--color-heading)" />
          <circle r="1.5" fill="var(--color-bg)" />
        </g>
      </svg>
    );
  }

  if (theme === 'minimal') {
    return (
      <svg viewBox="0 0 200 120" className={className} aria-hidden>
        {[0, 1, 2].map((i) => (
          <g
            key={i}
            transform={`translate(${40 + i * 40} 20) rotate(${(i - 1) * 4} 30 45)`}
          >
            <rect x="0" y="0" width="40" height="60" fill="var(--color-surface)" stroke="currentColor" strokeWidth="0.6" />
            <line x1="4" y1="8" x2="14" y2="8" stroke="var(--color-muted)" strokeWidth="0.5" />
            <circle cx="20" cy="30" r="6" fill="none" stroke="currentColor" strokeWidth="0.6" />
            {i === 0 && <path d="M14 30 L26 30 M20 24 L20 36" stroke="currentColor" strokeWidth="0.6" />}
            {i === 1 && (
              <polygon
                points="20,24 24,36 16,36"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.6"
              />
            )}
            {i === 2 && <circle cx="20" cy="30" r="2" fill="var(--color-accent)" />}
            <line x1="4" y1="52" x2="36" y2="52" stroke="var(--color-muted)" strokeWidth="0.5" />
          </g>
        ))}
      </svg>
    );
  }

  // morandi — stacked pebble cards
  return (
    <svg viewBox="0 0 220 150" className={className} aria-hidden>
      <g transform="translate(60 25) rotate(-8)">
        <rect x="0" y="0" width="40" height="62" rx="8" fill="var(--color-accent)" opacity="0.65" />
      </g>
      <g transform="translate(95 18)">
        <rect x="0" y="0" width="40" height="62" rx="8" fill="var(--color-accent-3, var(--color-accent))" opacity="0.85" />
      </g>
      <g transform="translate(130 25) rotate(8)">
        <rect
          x="0"
          y="0"
          width="40"
          height="62"
          rx="8"
          fill="var(--color-accent-2)"
          opacity="0.65"
        />
      </g>
      <circle cx="110" cy="110" r="3" fill="var(--color-accent)" opacity="0.5" />
      <circle cx="90" cy="120" r="2" fill="var(--color-accent-2)" opacity="0.5" />
      <circle cx="130" cy="115" r="2.5" fill="var(--color-accent-3, var(--color-accent))" opacity="0.5" />
    </svg>
  );
}

/**
 * Decorative background overlay — landing / chat 用。
 *
 * - mystic    faint star field + radial glow
 * - morandi   two soft ceramic blobs
 * - minimal   nothing (clean paper)
 */
export function ThemeBackdrop({ theme }: { theme: Theme }) {
  if (theme === 'mystic') {
    return (
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 10%, rgba(201,162,74,0.08) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(80,60,140,0.16) 0%, transparent 50%)',
        }}
      >
        <svg width="100%" height="100%" className="absolute inset-0" style={{ opacity: 0.4 }}>
          {STAR_POSITIONS.map(([x, y, r], i) => (
            <circle
              key={i}
              cx={`${x}%`}
              cy={`${y}%`}
              r={r}
              fill="var(--color-heading)"
              opacity={0.5}
            />
          ))}
        </svg>
      </div>
    );
  }

  if (theme === 'morandi') {
    return (
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div
          className="absolute"
          style={{
            top: '-10%',
            right: '-15%',
            width: 320,
            height: 320,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(184,122,111,0.18) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute"
          style={{
            bottom: '15%',
            left: '-20%',
            width: 280,
            height: 280,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(138,154,130,0.14) 0%, transparent 70%)',
          }}
        />
      </div>
    );
  }

  return null;
}

const STAR_POSITIONS: Array<[number, number, number]> = [
  [8, 12, 0.6], [18, 24, 0.4], [32, 8, 0.5], [45, 18, 0.6], [58, 6, 0.4],
  [70, 22, 0.5], [86, 14, 0.7], [92, 32, 0.4], [12, 42, 0.5], [28, 58, 0.4],
  [48, 46, 0.6], [72, 52, 0.4], [88, 66, 0.5], [18, 78, 0.4], [38, 86, 0.5],
  [56, 72, 0.6], [78, 84, 0.4], [6, 64, 0.5],
];

/**
 * Persona motif — onboarding picker card 上面的抽象 SVG（取代色塊 placeholder）。
 *
 * Spec §5 onboarding：「avatar 沒生圖前用 SVG 符號（Luna=月、Orion=星、Sage=幾何、??=遮罩）」
 * 同一支 motif 不分 theme — 只是 stroke 顏色跟著 theme 走。
 */
export function PersonaMotif({
  persona,
  className,
}: {
  persona: 'luna' | 'orion' | 'sage' | 'silhouette';
  className?: string;
}) {
  const stroke = 'currentColor';

  if (persona === 'luna') {
    // 月相 — 三個月相連線
    return (
      <svg viewBox="0 0 120 80" className={className} aria-hidden>
        <circle cx="20" cy="40" r="14" fill="none" stroke={stroke} strokeWidth="0.8" opacity="0.6" />
        <path
          d="M 60 26 A 14 14 0 1 1 60 54 A 10 14 0 1 0 60 26 Z"
          fill={stroke}
          opacity="0.85"
        />
        <circle cx="100" cy="40" r="14" fill={stroke} opacity="0.6" />
        <line x1="34" y1="40" x2="46" y2="40" stroke={stroke} strokeWidth="0.5" opacity="0.4" />
        <line x1="74" y1="40" x2="86" y2="40" stroke={stroke} strokeWidth="0.5" opacity="0.4" />
      </svg>
    );
  }

  if (persona === 'orion') {
    // 北斗七星 — Big Dipper constellation
    return (
      <svg viewBox="0 0 120 80" className={className} aria-hidden>
        {[
          [22, 56], [38, 50], [54, 44], [70, 40],
          [82, 26], [94, 22], [104, 36],
        ].map(([cx, cy], i, arr) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r={i === 0 || i === 6 ? 2.6 : 2} fill={stroke} />
            {i < arr.length - 1 && (
              <line
                x1={cx}
                y1={cy}
                x2={arr[i + 1]![0]}
                y2={arr[i + 1]![1]}
                stroke={stroke}
                strokeWidth="0.5"
                opacity="0.45"
              />
            )}
          </g>
        ))}
        <circle cx="14" cy="20" r="0.8" fill={stroke} opacity="0.5" />
        <circle cx="100" cy="62" r="0.8" fill={stroke} opacity="0.5" />
        <circle cx="60" cy="14" r="0.6" fill={stroke} opacity="0.4" />
      </svg>
    );
  }

  if (persona === 'sage') {
    // 幾何 — 三角 + 圓 + 方
    return (
      <svg viewBox="0 0 120 80" className={className} aria-hidden>
        <polygon
          points="22,58 40,28 58,58"
          fill="none"
          stroke={stroke}
          strokeWidth="0.9"
        />
        <circle cx="76" cy="42" r="14" fill="none" stroke={stroke} strokeWidth="0.9" />
        <rect x="86" y="50" width="22" height="22" fill="none" stroke={stroke} strokeWidth="0.9" />
        <line x1="40" y1="28" x2="40" y2="58" stroke={stroke} strokeWidth="0.4" opacity="0.4" />
        <line x1="62" y1="42" x2="90" y2="42" stroke={stroke} strokeWidth="0.4" opacity="0.4" />
      </svg>
    );
  }

  // silhouette — 半剪影（？遮罩感）
  return (
    <svg viewBox="0 0 120 80" className={className} aria-hidden>
      <defs>
        <linearGradient id="silhouette-fade" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.6" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <path
        d="M 38 70 C 38 50 46 38 60 38 C 74 38 82 50 82 70 Z"
        fill="url(#silhouette-fade)"
      />
      <circle cx="60" cy="28" r="10" fill="url(#silhouette-fade)" />
      <text
        x="60"
        y="52"
        textAnchor="middle"
        fontFamily="var(--font-display)"
        fontSize="22"
        fill="var(--color-bg)"
        opacity="0.55"
      >
        ?
      </text>
    </svg>
  );
}
